import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import clsx from 'clsx';

// ── Row ──────────────────────────────────────────────────────────────────────

export function Row({ label, tooltip, children }: { label: string; tooltip?: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="w-[92px] shrink-0 truncate text-[11px] text-ink-300" title={tooltip ?? label}>
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

// ── Collapsible group ────────────────────────────────────────────────────────

export function Group({
  title,
  defaultOpen = true,
  right,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  right?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-ink-800 last:border-b-0">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-1.5 py-2.5 text-left transition-colors hover:text-ink-100"
        >
          <ChevronRight size={12} className={clsx('shrink-0 text-ink-500 transition-transform', open && 'rotate-90')} />
          <span className="panel-label">{title}</span>
        </button>
        {right}
      </div>
      {open && <div className="pb-3 pl-0.5 pr-0.5">{children}</div>}
    </section>
  );
}

// ── Toggle ───────────────────────────────────────────────────────────────────

export function Toggle({
  checked,
  onChange,
  label,
  tooltip,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  tooltip?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      title={tooltip}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'flex w-full items-center gap-2 py-1 text-left transition-opacity',
        disabled && 'pointer-events-none opacity-40',
      )}
    >
      <span
        className={clsx(
          'relative h-[14px] w-[24px] shrink-0 rounded-full transition-colors',
          checked ? 'bg-accent-500' : 'bg-ink-700',
        )}
      >
        <span
          className={clsx(
            'absolute top-[2px] h-[10px] w-[10px] rounded-full bg-ink-950 transition-all',
            checked ? 'left-[12px]' : 'left-[2px]',
          )}
        />
      </span>
      <span className="flex-1 truncate text-[11px] text-ink-300">{label}</span>
    </button>
  );
}

// ── Segmented control ────────────────────────────────────────────────────────

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  title?: string;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly SegmentOption<T>[];
  size?: 'sm' | 'md';
}): JSX.Element {
  return (
    <div className="flex gap-0.5 rounded-md bg-ink-850 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.title ?? o.label}
          onClick={() => onChange(o.value)}
          className={clsx(
            'min-w-0 flex-1 truncate rounded px-1.5 transition-colors',
            size === 'sm' ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]',
            value === o.value ? 'bg-ink-700 text-ink-100' : 'text-ink-400 hover:text-ink-200',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Select ───────────────────────────────────────────────────────────────────

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** Renders inside an <optgroup>, so long lists stay navigable. */
  group?: string;
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly SelectOption<T>[];
  disabled?: boolean;
}): JSX.Element {
  const groups = new Map<string, SelectOption<T>[]>();
  for (const o of options) {
    const key = o.group ?? '';
    const list = groups.get(key);
    if (list) list.push(o);
    else groups.set(key, [o]);
  }

  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as T)}
      className="field-input cursor-pointer appearance-none bg-[length:10px] pr-6 text-[11px]"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6' fill='none' stroke='%236b6b7c' stroke-width='1.5'%3E%3Cpath d='M1 1l4 4 4-4'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
      }}
    >
      {[...groups.entries()].map(([groupName, items]) =>
        groupName ? (
          <optgroup key={groupName} label={groupName}>
            {items.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ) : (
          items.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))
        ),
      )}
    </select>
  );
}

// ── 9-point anchor grid ──────────────────────────────────────────────────────

export function AnchorGrid({
  x,
  y,
  onChange,
}: {
  x: 'left' | 'center' | 'right';
  y: 'top' | 'middle' | 'bottom';
  onChange: (x: 'left' | 'center' | 'right', y: 'top' | 'middle' | 'bottom') => void;
}): JSX.Element {
  const xs = ['left', 'center', 'right'] as const;
  const ys = ['top', 'middle', 'bottom'] as const;
  return (
    <div className="inline-grid grid-cols-3 gap-0.5 rounded-md bg-ink-850 p-0.5">
      {ys.map((yy) =>
        xs.map((xx) => {
          const active = x === xx && y === yy;
          return (
            <button
              key={`${xx}-${yy}`}
              type="button"
              title={`${xx} / ${yy}`}
              onClick={() => onChange(xx, yy)}
              className={clsx(
                'flex h-[18px] w-[18px] items-center justify-center rounded transition-colors',
                active ? 'bg-ink-700' : 'hover:bg-ink-800',
              )}
            >
              <span className={clsx('h-[4px] w-[4px] rounded-full', active ? 'bg-accent-500' : 'bg-ink-500')} />
            </button>
          );
        }),
      )}
    </div>
  );
}

// ── Colour ───────────────────────────────────────────────────────────────────

export function ColorInput({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <label className="relative h-[22px] w-[30px] shrink-0 cursor-pointer overflow-hidden rounded border border-ink-700">
        <span className="absolute inset-0" style={{ background: value }} />
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="field-input font-mono text-[11px] uppercase"
        spellCheck={false}
      />
    </div>
  );
}
